import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, {
    useEffect,
    useContext,
    useRef,
    Fragment,
    createRef,
    useReducer,
} from "react";
import { AppContext } from "context/App";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import SelectBox from "components/FormElements/SelectBox";
import { useSelector } from "react-redux";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import DangerButton from "components/Buttons/DangerButton";
import AutocompleteInput from "components/FormElements/AutocompleteInput";
import { useAuth } from "hooks/Auth";
import {
    apiGet,
    apiPost,
    apiPut,
    apiDel,
    messageFromException,
} from "helpers/Request";
import { transform as authorTransformer } from "schemas/Author";
import { transform as tagTransformer } from "schemas/Tag";
import { transform as userTransformer } from "schemas/User";
import { transform as collectionTransformer } from "schemas/Collection";
import { useLocation, useHistory } from "react-router-dom";
import { nonce } from "Utils";
import { transform as contentTransformer } from "schemas/Content";
import LoadingIndicator from "components/General/LoadingIndicator";
import { getPaginationMeta } from "schemas/Util";
import Pagination from "components/General/Pagination";
import { useContextualHelp } from "hooks/Help";
import {
    SupportIcon,
    PlusIcon,
    RefreshIcon,
    CollectionIcon,
    TagIcon,
    TrashIcon,
    CloudDownloadIcon,
    CloudUploadIcon,
    DocumentDownloadIcon,
} from "@heroicons/react/outline";
import { fromSqlDateTime } from "helpers/Date";
import ContentTable from "components/DesignSystem/ContentTable";
import { toast } from "react-hot-toast";
import FastTextInput from "components/FormElements/FastTextInput";
import CollectionBrowser from "./CollectionBrowser";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarMenu, {
    ToolbarMenuItem,
    ToolbarMenuItems,
} from "components/Toolbar/ToolbarMenu";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import { modal } from "components/HeadlessModal";
import { SQL_DATE_TIME_FORMAT, fromNow } from "helpers/Date";
// import ContentGrid from "components/DesignSystem/ContentGrid";

const perPage = 40;

const exifValues = [
    "Select",
    "AuthorByline",
    "Caption",
    "CaptionWriter",
    "Category",
    "City",
    "Country",
    "DocumentTitle",
    "ExposureMode",
    "ExposureTime",
    "FileName",
    "FileSource",
    "FNumber",
    "FocalLength",
    "GainControl",
    "ISOSpeedRatings",
    "Make",
    "Model",
    "PhotoSource",
    "ShutterSpeedValue",
    "Source",
    "State",
    "Subcategories",
];

const typeValues = [
    "Select",
    "Article",
    "Page",
    "Media",
    "Entry",
    "Image",
    "Gallery",
    "Video",
    "Audio",
    "PDF",
];

const defaultValues = {
    keyword: "",
    published_after: "",
    published_before: "",
    created_after: "",
    created_before: "",
    image_keyword: "",
    image_metadata_label: null,
    image_metadata_value: "",
    author: [],
    tag: [],
    edited_by: [],
    content_type: null,
    workflow: null,
    nonce: null,
    per_page: perPage,
    page: 1,
};

function serialize(data) {
    const { author, tag, edited_by, ...rest } = data;
    const out = {
        author: author.map((a) => `${a.value}::${a.label}`),
        tag: tag.map((t) => `${t.value}::${t.label}`),
        edited_by: edited_by.map((u) => `${u.value}::${u.label}`),
        ...rest,
    };

    return new URLSearchParams(out).toString();
}

function deserialize(data) {
    const params = new URLSearchParams(data);
    const keys = Object.keys(defaultValues);
    const out = {};
    keys.forEach((key) => {
        if (params.has(key) && params.get(key) && params.get(key) !== "null") {
            out[key] = params.get(key);
        }
    });

    // specials
    if (out.workflow) {
        out.workflow = parseInt(out.workflow);
    }
    if (out.author) {
        out.author = deserializeMap(out.author);
    }
    if (out.tag) {
        out.tag = deserializeMap(out.tag);
    }
    if (out.edited_by) {
        out.edited_by = deserializeMap(out.edited_by);
    }

    return Object.assign({}, defaultValues, out);
}

function deserializeMap(data) {
    if (!data || !data.length) {
        return [];
    }
    return data.split(",").map((a) => {
        const [value, label] = a.split("::");
        return { value, label };
    });
}

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

export default function SearchScreen(props) {
    const { setCurrentNav, setSearchNav } = useContext(AppContext);
    const { workflows } = useSelector((state) => state.contentFlow);
    const location = useLocation();
    const history = useHistory();
    const auth = useAuth();
    const [state, setState] = useReducer(reducer, {
        items: [],
        searching: false,
        didSearch: false,
        initialValues: defaultValues,
        paginationMeta: [],
        selectedItems: [],
        lastUpdate: false,
    });

    const contextualHelp = useContextualHelp();

    const formRef = useRef();

    const onSubmit = (values) => {
        history.push(
            location.pathname +
                "?" +
                serialize(Object.assign({}, values, { nonce: nonce() }))
        );
    };

    const onReset = () => {
        setSearchNav("/app/search");
        formRef.current.resetForm();
        setState({
            initialValues: defaultValues,
            items: [],
            paginationMeta: [],
            selectedItems: [],
            didSearch: false,
        });
    };

    const runSearch = (values) => {
        setState({ selectedItems: [], searching: true, didSearch: true });
        apiGet(
            "/v4/search/",
            auth.publication,
            Object.assign({}, values, { type: "firehose" })
        )
            .then((resp) => {
                setState({
                    items: contentTransformer(resp),
                    paginationMeta: getPaginationMeta(resp),
                    searching: false,
                });
                if (
                    resp.meta?.last_update &&
                    resp.meta?.last_update !== "unknown"
                ) {
                    setState({
                        lastUpdate: fromSqlDateTime(resp.meta.last_update),
                    });
                }
            })
            .catch((err) => {
                setState({ searching: false, didSearch: false });
                modal.error({
                    title: "There was an error running your search",
                    children: messageFromException(err),
                });
            });
    };

    const getEncodedSearch = () => {
        const n = "%0A";
        try {
            return (
                n +
                n +
                encodeURIComponent(
                    "---- Please keep the following to help us investigate the problem ----"
                ) +
                n +
                encodeURIComponent(
                    `Client Code: ${auth.publication.client_code}`
                ) +
                n +
                encodeURIComponent(`Search Data: ${btoa(location.search)}`) +
                n +
                encodeURIComponent("------- END DEBUG ------") +
                n
            );
        } catch (e) {
            console.error(e);
            return "";
        }
    };

    const onSingleSelect = (item, checked) => {
        if (checked) {
            setState({ selectedItems: [...state.selectedItems, item.uuid] });
        } else {
            setState({
                selectedItems: state.selectedItems.filter(
                    (i) => i !== item.uuid
                ),
            });
        }
    };
    const onAllSelect = (items) => {
        setState({ selectedItems: items.map((item) => item.uuid) });
    };

    const handleTag = () => {
        const formRef = createRef();
        modal({
            title: "Tag selected items",
            children: (
                <Formik
                    initialValues={{ tags: [] }}
                    innerRef={formRef}
                    onSubmit={(values) => {
                        const data = {
                            tags: values.tags.map((tag) => tag.value),
                        };
                        modal.update({
                            disabled: true,
                        });
                        const id = toast.loading("Tagging...");
                        const promises = state.selectedItems.map((uuid) =>
                            apiPut(`/v4/content/${uuid}`, auth.publication, {
                                tags: data.tags,
                                acquire_lock: true,
                                append_tags: true,
                            })
                        );
                        Promise.all(promises)
                            .then(() => {
                                modal.dismiss();
                                toast.success("Content updated", { id: id });
                            })
                            .catch((err) => {
                                modal.dismiss();
                                toast.dismiss(id);
                            });
                    }}
                >
                    <Form>
                        <AutocompleteInput
                            label="Search tags:"
                            id="tags"
                            name="tags"
                            onRequestSearch={async (term) => {
                                const items = await apiGet(
                                    "/v4/search",
                                    auth.publication,
                                    {
                                        keyword: term,
                                        per_page: 5,
                                        type: "tag",
                                    }
                                );

                                return tagTransformer(items).map((item) => {
                                    return {
                                        label: item.name,
                                        value: item.uuid,
                                    };
                                });
                            }}
                        />
                        <div className="my-10">
                            <p className="prose-sm text-gray-400">
                                Tags will be appended to content, existing tags
                                will not be replaced.
                            </p>
                        </div>
                    </Form>
                </Formik>
            ),
            buttons: ({ disabled }) => (
                <Fragment>
                    <PrimaryButton
                        disabled={disabled}
                        onClick={() => {
                            formRef.current.submitForm();
                        }}
                    >
                        Update Tags
                    </PrimaryButton>
                    <SecondaryButton
                        disabled={disabled}
                        onClick={modal.dismiss}
                    >
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const collectionRef = createRef();
    const handleCollection = () => {
        modal({
            title: "Add to collections",
            children: (
                <Formik
                    initialValues={{ collections: [] }}
                    onSubmit={onAddToCollection}
                    innerRef={collectionRef}
                >
                    <Form>
                        <div className="flex">
                            <div className="flex-grow">
                                <AutocompleteInput
                                    label="Search collections:"
                                    id="collections"
                                    name="collections"
                                    onRequestSearch={onRequestSearchCollections}
                                />
                            </div>
                        </div>
                    </Form>
                </Formik>
            ),
            buttons: () => (
                <Fragment>
                    <PrimaryButton
                        onClick={() => {
                            collectionRef.current.submitForm();
                        }}
                    >
                        Add to collection
                    </PrimaryButton>
                    <SecondaryButton
                        type="button"
                        onClick={() => {
                            modal.dismiss();
                            handleCreateCollection();
                        }}
                    >
                        <PlusIcon className="h-4 w-4" />
                    </SecondaryButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const onRequestSearchCollections = async (term) => {
        const items = await apiGet("/v4/search", auth.publication, {
            keyword: term,
            per_page: 5,
            type: "collection",
        });

        return collectionTransformer(items).map((item) => {
            return {
                label: item.name,
                value: item.uuid,
            };
        });
    };

    const onAddToCollection = (values) => {
        const data = {
            collections: values.collections.map(
                (collection) => collection.value
            ),
        };
        modal.update({
            disabled: true,
        });
        const id = toast.loading("Adding to collections...");

        const promises = state.selectedItems.map((uuid) =>
            apiPut(`/v4/content/${uuid}`, auth.publication, {
                collections: data.collections,
                acquire_lock: true,
                append_collections: true,
            })
        );

        Promise.all(promises)
            .then(() => {
                modal.dismiss();
                toast.success("Content updated", { id });
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to update content",
                    children: messageFromException(err),
                });
            });
    };

    const newCollectionRef = createRef();
    const handleCreateCollection = () => {
        modal({
            title: "Add to new collection",
            children: (
                <Formik
                    initialValues={{ name: "" }}
                    innerRef={newCollectionRef}
                    onSubmit={onCreateCollection}
                >
                    <Form>
                        <FastTextInput
                            name="name"
                            id="name"
                            placeholder="New Collection"
                        />
                    </Form>
                </Formik>
            ),
            buttons: () => (
                <Fragment>
                    <PrimaryButton
                        onClick={() => {
                            newCollectionRef.current.submitForm();
                        }}
                    >
                        Create collection
                    </PrimaryButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const onCreateCollection = async (values) => {
        const data = {
            name: values.name,
        };

        const id = toast.loading("Creating collection...");
        modal.update({ disabled: true });
        const collection = await apiPost(
            "/v4/collection/",
            auth.publication,
            data
        ).then((resp) => collectionTransformer(resp));

        const promises = state.selectedItems.map((uuid) =>
            apiPut(`/v4/content/${uuid}`, auth.publication, {
                collections: [collection.uuid],
                acquire_lock: true,
                append_collections: true,
            })
        );
        Promise.all(promises)
            .then(() => {
                modal.dismiss();
                toast.success("Content updated", { id });
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to update content",
                    children: messageFromException(err),
                });
            });
    };

    const exportAs = (type) => {
        const url = "/v4/export/as/" + type;

        apiPost(url, auth.publication, state.selectedItems, true, null, {
            responseType: "arraybuffer",
        })
            .then((resp) => {
                if (!window) {
                    return;
                }
                let ext = "zip";
                let data = resp;

                const fileName = `ceo-export.${ext}`;
                // create the data url, inject, click and remove
                const earl = window.URL.createObjectURL(
                    new Blob([data], { type: "application/zip" })
                );
                const tempLink = document.createElement("a");
                tempLink.href = earl;
                tempLink.setAttribute("download", fileName);
                tempLink.setAttribute("target", "_blank");
                document.body.appendChild(tempLink);
                tempLink.click();
                document.body.removeChild(tempLink);
                toast.success("Content exported");
            })
            .catch((err) => {
                toast.error(err.message);
            });
    };

    const onRequestPublish = () => {
        const data = {
            forced: true,
            published_at: fromNow().toUTC().toFormat(SQL_DATE_TIME_FORMAT),
        };

        onRequestUpdate(data);
    };

    const onRequestUnpublish = () => {
        const data = {
            forced: true,
            published_at: null,
        };

        onRequestUpdate(data);
    };

    const onRequestDelete = () => {
        modal.warning({
            title: "Delete Items",
            children: (
                <p>
                    Are you sure you want to delete the selected items? This
                    cannot be undone.
                </p>
            ),
            buttons: ({ disabled }) => (
                <Fragment>
                    <DangerButton
                        disabled={disabled}
                        onClick={() => {
                            const id = toast.loading("Deleting...");
                            modal.update({ disabled: true });
                            handleDelete().finally(() => {
                                toast.dismiss(id);
                                modal.dismiss();
                            });
                        }}
                    >
                        Delete
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const handleDelete = async () => {
        const promises = state.selectedItems.map((uuid) =>
            apiDel(`/v4/content/${uuid}`, auth.publication)
        );
        return Promise.all(promises)
            .then(() => {
                toast.success("Content deleted");
                runSearch(deserialize(location.search));
            })
            .catch((err) => {
                console.error(err);
                modal.update({
                    title: "Unable to delete those items",
                    children: (
                        <Fragment>
                            CEO was unable to delete those items, usually this
                            is because you do not have permission to delete.
                        </Fragment>
                    ),
                });
            });
    };

    const onRequestUpdate = (data) => {
        const id = toast.loading("Updating...");

        const promises = state.selectedItems.map((uuid) =>
            apiPut(`/v4/content/${uuid}`, auth.publication, data)
        );
        Promise.all(promises)
            .then(() => {
                toast.success("Updated!", { id });
                runSearch(deserialize(location.search));
            })
            .catch((err) => {
                modal.error({
                    title: "Unable to update selected items",
                    children: messageFromException(err),
                });
                toast.dismiss();
            });
    };

    useEffect(() => {
        const vals = deserialize(location.search);
        if (!vals.nonce) {
            // if there's no nonce, then the search field is empty and we don't want to run a search
            return;
        }
        if (state.initialValues.nonce === null) {
            // if the initial values don't have a nonce, but we do have values, then set the initial values
            setState({
                initialValues: Object.assign({}, vals, {
                    nonce: 1,
                }),
            });
        }

        setSearchNav("/app/search" + location.search);
        runSearch(vals);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    useEffect(() => {
        setCurrentNav("search");
    }, [setCurrentNav]);

    return (
        <MainBlock>
            <Formik
                innerRef={formRef}
                onSubmit={onSubmit}
                enableReinitialize={true}
                initialValues={state.initialValues}
            >
                <Form>
                    <div className="rounded-md bg-white p-4 shadow-sm">
                        <div className="mx-auto gap-4 space-y-4 lg:grid lg:auto-rows-max lg:grid-cols-2 lg:space-y-0">
                            <div>
                                <TextInput
                                    name="keyword"
                                    label="Keywords"
                                    placeholder="fuzzy puppies frolic in the park"
                                />
                            </div>
                            <div>
                                <SelectBox
                                    name="content_type"
                                    options={typeValues}
                                    label="Type"
                                />
                            </div>
                            <div>
                                <AutocompleteInput
                                    label="Author"
                                    name="author"
                                    onRequestSearch={async (term) => {
                                        const items = await apiGet(
                                            "/v4/search",
                                            auth.publication,
                                            {
                                                keyword: term,
                                                per_page: 5,
                                                type: "author",
                                            }
                                        );

                                        return authorTransformer(items).map(
                                            (item) => {
                                                return {
                                                    label: item.name,
                                                    value: item.uuid,
                                                };
                                            }
                                        );
                                    }}
                                />
                            </div>
                            <div>
                                <AutocompleteInput
                                    label="Tag"
                                    name="tag"
                                    onRequestSearch={async (term) => {
                                        const items = await apiGet(
                                            "/v4/search",
                                            auth.publication,
                                            {
                                                keyword: term,
                                                per_page: 5,
                                                type: "tag",
                                            }
                                        );

                                        return tagTransformer(items).map(
                                            (item) => {
                                                return {
                                                    label: item.name,
                                                    value: item.uuid,
                                                };
                                            }
                                        );
                                    }}
                                />{" "}
                            </div>
                            <div>
                                <AutocompleteInput
                                    label="Edited By"
                                    name="edited_by"
                                    onRequestSearch={async (term) => {
                                        const items = await apiGet(
                                            "/v4/search",
                                            auth.publication,
                                            {
                                                keyword: term,
                                                per_page: 5,
                                                type: "user",
                                                includesnw: 1,
                                            }
                                        );

                                        return userTransformer(items).map(
                                            (item) => {
                                                return {
                                                    label: item.name,
                                                    value: item.uuid,
                                                };
                                            }
                                        );
                                    }}
                                />
                            </div>
                            <div>
                                <SelectBox
                                    name="workflow"
                                    options={workflows.map((workflow) => ({
                                        value: workflow.id,
                                        label: workflow.name,
                                    }))}
                                    label="Workflow"
                                />
                            </div>
                            <div className="flex space-x-4">
                                <div className="flex-grow">
                                    <TextInput
                                        name="published_after"
                                        label="Published After"
                                        type="date"
                                    />
                                </div>
                                <div className="flex-grow">
                                    <TextInput
                                        name="published_before"
                                        label="Published Before"
                                        type="date"
                                        className="flex-grow"
                                    />
                                </div>
                            </div>
                            <div className="flex space-x-4">
                                <div className="flex-grow">
                                    <TextInput
                                        name="created_after"
                                        label="Created After"
                                        type="date"
                                    />
                                </div>
                                <div className="flex-grow">
                                    <TextInput
                                        name="created_before"
                                        label="Created Before"
                                        type="date"
                                        className="flex-grow"
                                    />
                                </div>
                            </div>
                            <div>
                                <TextInput
                                    name="image_keyword"
                                    label="Image Contains"
                                    placeholder="dog"
                                    helpertext="For example: 'cat', 'football', 'plane', 'table'"
                                />
                            </div>
                            <div className="flex space-x-4">
                                <div className="flex-grow">
                                    <SelectBox
                                        name="image_metadata_label"
                                        options={exifValues}
                                        label="EXIF/IPTC"
                                    />
                                </div>
                                <div className="flex-grow">
                                    <TextInput
                                        name="image_metadata_value"
                                        label="Keyword"
                                        placeholder="Central Park"
                                        helpertext="For example: 'USA', 'NIKON D300'"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mx-auto mt-4 flex max-w-sm space-x-2">
                            <PrimaryButton
                                type="submit"
                                className="flex-grow"
                                disabled={state.searching}
                            >
                                Search CEO
                            </PrimaryButton>
                            <SecondaryButton type="button" onClick={onReset}>
                                Reset
                            </SecondaryButton>
                            <button
                                type="button"
                                className="flex items-center space-x-4 text-sm text-green-600"
                                onClick={() => {
                                    contextualHelp.showContextualHelp(
                                        "/search/index.html"
                                    );
                                }}
                            >
                                <SupportIcon className="h-4 w-4" /> Help
                            </button>
                        </div>
                    </div>
                </Form>
            </Formik>
            <div>
                {state.searching && <LoadingIndicator />}
                {!state.didSearch && <CollectionBrowser />}
                {state.didSearch && !state.searching && (
                    <div className="mt-4 space-y-4">
                        <Pagination
                            basePath="search"
                            {...state.paginationMeta}
                        />
                        <div className="text-center text-sm  text-gray-800">
                            Showing{" "}
                            <strong>
                                {perPage *
                                    parseInt(
                                        state.paginationMeta.current_page
                                    ) -
                                    perPage +
                                    1}{" "}
                                -{" "}
                                {state.paginationMeta.current_page ===
                                state.paginationMeta.total_pages
                                    ? state.paginationMeta.total
                                    : perPage *
                                      parseInt(
                                          state.paginationMeta.current_page
                                      )}
                            </strong>{" "}
                            of <strong>{state.paginationMeta.total}</strong>{" "}
                            results.
                            {state.lastUpdate && (
                                <span>
                                    {" "}
                                    Last update{" "}
                                    {state.lastUpdate.toFormat("ff")}.
                                </span>
                            )}
                        </div>
                        {/*<div className="rounded-md bg-white p-4 shadow-md">
                            <ContentGrid items={items} />
                        </div>*/}

                        <Toolbar>
                            <ToolbarGroup>
                                <ToolbarButton
                                    icon={<RefreshIcon />}
                                    onClick={() => {
                                        runSearch(deserialize(location.search));
                                    }}
                                />
                            </ToolbarGroup>
                            <ToolbarGroup className="flex-grow">
                                <ToolbarButton
                                    icon={<TrashIcon />}
                                    disabled={!state.selectedItems.length}
                                    onClick={onRequestDelete}
                                >
                                    Delete
                                </ToolbarButton>
                                <ToolbarButton
                                    icon={<CloudUploadIcon />}
                                    disabled={!state.selectedItems.length}
                                    onClick={onRequestPublish}
                                >
                                    Publish
                                </ToolbarButton>
                                <ToolbarButton
                                    icon={<CloudDownloadIcon />}
                                    disabled={!state.selectedItems.length}
                                    onClick={onRequestUnpublish}
                                >
                                    Unpublish
                                </ToolbarButton>
                                <ToolbarButton
                                    icon={<TagIcon />}
                                    disabled={!state.selectedItems.length}
                                    onClick={handleTag}
                                >
                                    Tag
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={handleCollection}
                                    disabled={!state.selectedItems.length}
                                    icon={<CollectionIcon />}
                                >
                                    Collection
                                </ToolbarButton>
                                <ToolbarMenu
                                    icon={<DocumentDownloadIcon />}
                                    disabled={!state.selectedItems.length}
                                    label="Export"
                                >
                                    <ToolbarMenuItems>
                                        <ToolbarMenuItem
                                            onClick={() => exportAs("html")}
                                        >
                                            Plain HTML
                                        </ToolbarMenuItem>
                                        <ToolbarMenuItem
                                            onClick={() => exportAs("text")}
                                        >
                                            Plain Text
                                        </ToolbarMenuItem>
                                        <ToolbarMenuItem
                                            onClick={() => exportAs("icml")}
                                        >
                                            InCopy/InDesign
                                        </ToolbarMenuItem>
                                        <ToolbarMenuItem
                                            onClick={() =>
                                                exportAs("simplifiedxml")
                                            }
                                        >
                                            Simplified XML
                                        </ToolbarMenuItem>
                                        <ToolbarMenuItem
                                            onClick={() =>
                                                exportAs("applenews")
                                            }
                                        >
                                            Apple News Format
                                        </ToolbarMenuItem>
                                        <ToolbarMenuItem
                                            onClick={() => exportAs("ceo")}
                                        >
                                            CEO Export Format
                                        </ToolbarMenuItem>
                                        <ToolbarMenuItem
                                            onClick={() =>
                                                exportAs("ceointerchange")
                                            }
                                        >
                                            CEO API Interchange Format
                                        </ToolbarMenuItem>
                                    </ToolbarMenuItems>
                                </ToolbarMenu>
                            </ToolbarGroup>
                        </Toolbar>

                        <ContentTable
                            items={state.items}
                            selectedItems={state.selectedItems}
                            onSelect={onSingleSelect}
                            onSelectAll={onAllSelect}
                        />
                        <div className="text-center">
                            <a
                                className="cursor-pointer text-sm text-green-600 underline"
                                href={`mailto:support@getsnworks.com?subject=CEO%20Search%20Feedback&body=${getEncodedSearch()}`}
                            >
                                Not finding what you need? Let us know so we can
                                improve search.
                            </a>
                        </div>
                        <Pagination
                            basePath="search"
                            {...state.paginationMeta}
                        />
                    </div>
                )}
            </div>
        </MainBlock>
    );
}
